import { FC, useEffect, useState } from 'react';
import { Loader } from '@livechat/design-system-react-components';
import Cookies from 'js-cookie';

import Config from 'Config';
import { SigninFormData, FormStates } from 'types/form';
import { Product } from 'types/product';
import { useNotifications } from 'contexts/notifications/use-notifications';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { useGetMe } from 'hooks/use-get-me';
import Location from 'services/Location';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { accountsClient } from 'services/connectivity/accounts/client';
import { submitPost } from 'helpers/form';
import { getAccessToken } from 'helpers/account';
import { validatePassword } from 'helpers/validators/password';
import { parseJwt } from 'helpers/token';

import { NewPassword } from './master-password/NewPassword';
import * as styles from './styles';

interface IState {
  formData: SigninFormData;
  formState: string;
  token: string;
  error?: string;
  validationError?: string;
}

export const Wizard: FC = () => {
  const [state, setState] = useState<IState>({
    formState: FormStates.Default,
    formData: {},
    error: null,
    token: getAccessToken(),
  });

  const { showToast } = useNotifications();
  const { email, isFetching } = useGetMe();
  const wizardState = Location.getHashParam('state') || Location.getUrlParam('state');
  const params = wizardState && parseJwt(wizardState);
  const config = params || Config;

  if (Location.getUrlParam('entrypoint') === import.meta.env.VITE_APP_PP_ENTRYPOINT) {
    Cookies.set('product', Product.PartnerProgram, { expires: 365, sameSite: 'Lax' });
  }

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      formData: { email },
    }));
  }, [email]);

  const handlePasswordInputChange = (e) => {
    if (!validatePassword(e.target.value)) {
      setState((prevState) => ({
        ...prevState,
        validationError: '',
        formState: FormStates.Default,
      }));
    }
    setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        password: e.target.value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validatePassword(state.formData.password);
    if (validationError) {
      setState((prevState) => ({
        ...prevState,
        validationError,
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      formState: FormStates.Requesting,
    }));

    const { error } = await accountsClient.resetPassword(
      { password: state.formData.password },
      state.token
    );

    if (error) {
      showToast({
        content: "We couldn't change the password",
        variant: 'error',
      });

      return;
    }

    setState((prevState) => ({
      ...prevState,
      formState: FormStates.Success,
    }));

    showToast({
      content: 'Password successfully changed',
      variant: 'success',
    });

    submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(config)}`, {
      email: state.formData.email,
      password: state.formData.password,
    });
  };

  if (isFetching) {
    return <Loader data-testid="wizard-loader" size="large" css={styles.loaderContainer} />;
  }

  if (state.error) {
    return <UnexpectedError />;
  }

  return (
    <NewPassword
      formData={state.formData}
      formState={state.formState}
      onSubmit={handleSubmit}
      onPasswordInputChange={handlePasswordInputChange}
      validationError={state.validationError}
    />
  );
};
